import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../core/lib/supabase';
import { authService } from '../features/auth/services/authService';

interface OrganizationSettings {
  primary_color?: string;
  secondary_color?: string;
  logo_url?: string;
  name?: string;
}

interface OrganizationContextType {
  organizationId: string | null;
  settings: OrganizationSettings | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType>({
  organizationId: null,
  settings: null,
  loading: true,
  refetch: async () => {},
});

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
}

interface OrganizationProviderProps {
  children: React.ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOrganizationSettings = async () => {
    try {
      setLoading(true);
      
      // Obtener el usuario actual
      const session = await authService.getSession();
      if (!session?.user) {
        console.log('No authenticated user to load organization');
        setLoading(false);
        return;
      }

      // Obtener la organización del usuario desde el perfil
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('current_organization_id')
        .eq('id', session.user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        setLoading(false);
        return;
      }

      if (!profile?.current_organization_id) {
        console.log('User without assigned organization (current_organization_id)');
        setLoading(false);
        return;
      }

      setOrganizationId(profile.current_organization_id);

      // Obtener la configuración de la organización
      const { data: orgSettings, error: settingsError } = await supabase
        .from('organizations')
        .select('primary_color, secondary_color, logo_url, name')
        .eq('id', profile.current_organization_id)
        .single();

      if (settingsError) {
        console.error('Error fetching organization configuration:', settingsError);
      } else {
        console.log('✅ Organization configuration loaded:', orgSettings);
        setSettings(orgSettings);
      }
    } catch (error) {
      console.error('Error in fetchOrganizationSettings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizationSettings();

    // Suscribirse a cambios de autenticación
    const unsubscribe = authService.subscribe((state) => {
      if (state.user) {
        fetchOrganizationSettings();
      } else {
        setOrganizationId(null);
        setSettings(null);
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const value: OrganizationContextType = {
    organizationId,
    settings,
    loading,
    refetch: fetchOrganizationSettings,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}



